/**
 * App Overlay Slice
 *
 * App-level Redux state for the two full-screen blocking overlays (budget
 * exceeded / account suspended) plus the queue of "legal document updated"
 * modals.
 *
 * Flutter sources:
 * - `lib/domain/notifiers/app_overlay_notifier.dart:1-16` — the two booleans
 *   (`shouldDisplayOverPriceOverlay`, `shouldDisplayAccountSuspendedOverlay`)
 *   and the two setters that flip them to `true`. Flutter has no "hide" setter.
 * - `lib/presentation/screens/root/root_screen.dart:306-320` — the listener
 *   that reacts to those flags: it resets the flag back to `false` and inserts
 *   an `AppOverlay.budgetExceededOverlay()` / `accountSuspendedOverlay()`
 *   `OverlayEntry`, then pauses all videos.
 *
 * **Web mapping (deliberate, not a parity miss).** Flutter resets the flag to
 * `false` immediately because the inserted `OverlayEntry` keeps itself alive
 * independently of the notifier and removes itself from its own Close button
 * (`overlay_widget.dart:70-73`). On the web there is no detached overlay
 * entry — this Redux flag *is* the visibility state — so `show…` sets `true`
 * and the overlay's own Close button dispatches `hide…`. Same observable
 * behaviour, idiomatic mechanism.
 *
 * There is no auto-hide for the account-suspended overlay: Flutter's suspended
 * overlay has no Close affordance (`overlay_widget.dart:112-185`).
 * `hideAccountSuspendedOverlay` is retained only as a defensive no-op:
 * `LoginPage`'s `useProviderLogin` `onSuccess` handler still dispatches it, but
 * nothing reaches it any more. The overlay's own control navigates to
 * `/contact` *without* clearing the flag, and `AppOverlayHost` suppresses the
 * overlay by route instead (`APP_OVERLAY_CONSTANTS.accountSuspendedExemptPaths`);
 * `/login` is deliberately not exempt, so the overlay covers the login form for
 * the whole suspended session, and on a fresh page load the flag starts `false`
 * so the dispatch is a no-op anyway. That moves the web *toward* Flutter on this
 * one point: `AppOverlayNotifier`
 * (`lib/domain/notifiers/app_overlay_notifier.dart:1-16`) exposes only the two
 * setters that flip its flags to `true` and has no "hide" setter, as noted
 * above.
 *
 * **Producers → consumers**
 * - Producers: the Remote Config listener (`budgetExceeded` real-time push and
 *   the post-init check) dispatches `showBudgetExceededOverlay` /
 *   `enqueueUpdatedLegalDocument`; the budget gates in
 *   `Navigator.navigateToVideoCall`, `Navigator.navigateToLiveStreaming` and
 *   `AddContentSheet.show` dispatch `showBudgetExceededOverlay`; `LoginPage`
 *   dispatches `showAccountSuspendedOverlay` on a suspended-account outcome and
 *   `AppInitializer` dispatches it when the startup refresh of an existing
 *   session returns a suspended user (Flutter `MainScreen._refreshUser`,
 *   `lib/presentation/screens/main/main_screen.dart:300-304`).
 * - Consumers: `AppOverlayHost` renders the two overlays from
 *   `selectBudgetExceededVisible` / `selectAccountSuspendedVisible`, and the
 *   legal-document modal renders `selectPendingLegalDocument`, dispatching
 *   `dismissUpdatedLegalDocument` on close so the next queued document shows.
 *
 * **Not** part of the SecureStorage-mirroring slice family (see
 * `.claude/context/state-slices.md`): it holds no persisted user data and has
 * no listener owning its writes, so feature code dispatching these actions
 * directly is correct — the "never dispatch setters from feature code" rule
 * does not apply to this slice.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { VersionUpdatedLegalDocument } from '@/domain/enums/versionUpdatedLegalDocument';
import type { RootState } from '@/presentation/store';

export interface AppOverlayState {
  budgetExceededVisible: boolean;
  accountSuspendedVisible: boolean;
  /** FIFO queue — one entry per legal document whose version changed. */
  updatedLegalDocuments: VersionUpdatedLegalDocument[];
}

const initialState: AppOverlayState = {
  budgetExceededVisible: false,
  accountSuspendedVisible: false,
  updatedLegalDocuments: [],
};

const appOverlaySlice = createSlice({
  name: 'appOverlay',
  initialState,
  reducers: {
    showBudgetExceededOverlay: (state) => {
      state.budgetExceededVisible = true;
    },
    hideBudgetExceededOverlay: (state) => {
      state.budgetExceededVisible = false;
    },
    showAccountSuspendedOverlay: (state) => {
      state.accountSuspendedVisible = true;
    },
    hideAccountSuspendedOverlay: (state) => {
      state.accountSuspendedVisible = false;
    },
    /**
     * Appends a document to the modal queue, ignoring duplicates: a repeated
     * Remote Config push of the same `version*` key must not stack two modals
     * for the same document.
     */
    enqueueUpdatedLegalDocument: (
      state,
      action: PayloadAction<VersionUpdatedLegalDocument>,
    ) => {
      if (!state.updatedLegalDocuments.includes(action.payload)) {
        state.updatedLegalDocuments.push(action.payload);
      }
    },
    /** Drops the queue head — the document whose modal was just dismissed. */
    dismissUpdatedLegalDocument: (state) => {
      state.updatedLegalDocuments.shift();
    },
  },
});

export const {
  showBudgetExceededOverlay,
  hideBudgetExceededOverlay,
  showAccountSuspendedOverlay,
  hideAccountSuspendedOverlay,
  enqueueUpdatedLegalDocument,
  dismissUpdatedLegalDocument,
} = appOverlaySlice.actions;

export default appOverlaySlice.reducer;

// --- Selectors ---

export const selectBudgetExceededVisible = (state: RootState): boolean =>
  state.appOverlay.budgetExceededVisible;

export const selectAccountSuspendedVisible = (state: RootState): boolean =>
  state.appOverlay.accountSuspendedVisible;

/** The legal document whose modal is currently due, or `null` when none. */
export const selectPendingLegalDocument = (
  state: RootState,
): VersionUpdatedLegalDocument | null =>
  state.appOverlay.updatedLegalDocuments[0] ?? null;
